
import { useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CreateTaskParams {
  title: string;
  description?: string;
  priority: string;
  difficulty: string;
  assigned_to: string;
  brand_id?: string;
  project_id?: string;
  source_type: string;
  source_data?: any;
  template_name?: string;
  due_date?: Date;
  images?: string[];
  watchers?: string[];
  internal?: boolean;
  time_estimated?: number;
  attachments?: any[];
}

export const useCreateGrowthTask = () => {
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const createTask = async (params: CreateTaskParams) => {
    setIsCreating(true);
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Map difficulty values from UI to database constraints
      const mapDifficulty = (difficulty: string) => {
        switch (difficulty) {
          case 'Easy': return 'Low';
          case 'Medium': return 'Medium';
          case 'Hard': return 'High';
          default: return 'Medium';
        }
      };

      // Enhanced source_data with template_name if provided
      const enhancedSourceData = {
        ...params.source_data,
        ...(params.template_name && { template_name: params.template_name })
      };

      const { data, error } = await supabase
        .from('foundational_tasks')
        .insert({
          title: params.title,
          description: params.description,
          priority: params.priority,
          difficulty: mapDifficulty(params.difficulty),
          assigned_to: params.assigned_to,
          brand_id: params.brand_id,
          project_id: params.project_id || null,
          user_id: user.user.id,
          source_type: params.source_type,
          source_data: enhancedSourceData,
          status: 'pending',
          sprint_id: params.source_data?.sprint_id || null,
          due_date: params.due_date ? format(params.due_date, 'yyyy-MM-dd') : null,
          images: params.images || [],
          watchers: params.watchers || [],
          internal: params.internal || false,
          time_estimated: params.time_estimated || 0,
          attachments: params.attachments || []
        })
        .select()
        .single();

      if (error) throw error;

      // If this is a content idea task, update the content post with task_id
      if (params.source_data?.content_post_id && data.id) {
        const { error: updateError } = await supabase
          .from('cm_short_form_posts_generated')
          .update({ task_id: data.id })
          .eq('id', params.source_data.content_post_id);

        if (updateError) {
          console.error('Error updating content post with task_id:', updateError);
        }
      }

      // If this is a template task creation, update the sop_tasks table with foundational_task_id
      if (params.source_data?.sop_task_id && data.id) {
        const { error: updateError } = await supabase
          .from('sop_tasks')
          .update({ foundational_task_id: data.id })
          .eq('id', params.source_data.sop_task_id);

        if (updateError) {
          console.error('Error updating sop_tasks with foundational_task_id:', updateError);
        }
      }

      toast({
        title: "Success",
        description: "Task created successfully",
      });

      return data;
    } catch (error: any) {
      console.error('Error creating task:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create task",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createTask,
    isCreating
  };
};
